import {
  createRootRoute,
  createRoute,
  createRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useRouterState,
} from "@tanstack/react-router";
import { Header as PcHeader } from "./components/pc-design/BaseLayout/Header";
import { Header as SpHeader } from "./components/sp-design/BaseLayout/Header";
import { SimpleHeader as SpSimpleHeader } from "./components/sp-design/BaseLayout/SimpleHeader";
import { Fragment, lazy } from "react";
import { useEffect, useState } from "react";
import { AuthGuard, RoleGuard, RoleRedirect } from "./lib/authGuard";
import { isPatientSelfAuthEnabled } from "./lib/featureFlags";
import {
  PATIENT_HOME_PATH,
  PatientAuthentication,
  PatientSessionGuard,
  currentPatientSession,
} from "./features/patient-auth";
import { OwnPatientDetailProvider } from "./contexts/OwnPatientDetailContext";
import { TermsOfUseGuard } from "./lib/TermsOfUseGuard";
import { ReservationEligibilityGuard } from "./lib/ReservationEligibilityGuard";
import { PaymentRouteGuard } from "./lib/PaymentRouteGuard";
import { Menu } from "./components/pc-design";
import { clinicMenuItems } from "./routes/clinic/clinicMenuItems";
import { bizsolMenuItems } from "./routes/bizsol/bizsolMenuItems";
import { DocumentTitle } from "./components/DocumentTitle";
import { ClinicTermsOfUseGuard } from "./lib/ClinicTermsOfUseGuard";
import { CompanyAdminTermsOfUseGuard } from "./lib/CompanyAdminTermsOfUseGuard";
import { HeaderPc } from "./components/new-pc-design/HeaderPc";
import { SidebarMenu } from "./components/new-pc-design/SidebarMenu";
import type { MenuItemDef } from "./components/new-pc-design/SidebarMenu";

import Dashboard from "./assets/new-design/Icon/dashboard.svg";
import IdCard from "./assets/new-design/Icon/id_card.svg";
import Corporate from "./assets/new-design/Icon/corporate.svg";
import Notifications from "./assets/new-design/Icon/notifications.svg";
import Group from "./assets/new-design/Icon/group.svg";
import Settings from "./assets/new-design/Icon/settings.svg";
import Lock from "./assets/new-design/Icon/lock.svg";
import Book2 from "./assets/new-design/Icon/book_2.svg";

const companyAdminMenuSections: readonly (readonly MenuItemDef[])[] = [
  [
    {
      label: "ダッシュボード",
      icon: Dashboard,
      isLink: true,
      href: "/company-admin/dashboard",
    },
    {
      label: "従業員一覧",
      icon: IdCard,
      isLink: true,
      href: "/company-admin/employees",
    },
    {
      label: "事業所一覧",
      icon: Corporate,
      isLink: true,
      href: "/company-admin/offices",
    },
    /* お知らせを確認できる機能は9/16リリース対象外のため、該当ルートをコメントアウト
    {
      label: "お知らせ",
      icon: Notifications,
      isLink: true,
      href: "/company-admin/notifications",
    },
    */
  ],
  [
    /* 管理者自身でユーザーを追加できる機能は9/16リリース対象外のため、該当ルートをコメントアウト
    {
      label: "管理者設定",
      icon: Group,
      isLink: true,
      href: "/company-admin/admin-settings",
    },
    */
    {
      label: "基本設定",
      icon: Settings,
      isLink: true,
      href: "/company-admin/basic-settings",
    },
    {
      label: "パスワード変更",
      icon: Lock,
      isLink: true,
      href: "/company-admin/password-change",
    },
    {
      label: "リンク集",
      icon: Book2,
      isLink: true,
      href: "/company-admin/links",
    },
  ],
];

const RootComponent = () => (
  <div className="min-h-screen">
    <DocumentTitle />
    <Outlet />
  </div>
);

const rootRoute = createRootRoute({ component: RootComponent });

// レスポンシブ対応: 画面幅に応じてヘッダーを切り替え
const ResponsiveHeader = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const update = () => setIsMobile(window.innerWidth < 768);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  return isMobile ? <SpSimpleHeader /> : <PcHeader />;
};

const PaymentResultStatusRedirect = ({
  status,
}: {
  status: "pending" | "FAILED";
}) => {
  useEffect(() => {
    window.location.replace(`/patient/payment/result?status=${status}`);
  }, [status]);

  return null;
};

const Home = lazy(() => import("./routes/Home"));
const ForbiddenPage = lazy(() => import("./routes/Forbidden"));
const NotFound = lazy(() => import("./routes/NotFound"));
const ServerError = lazy(() => import("./routes/ServerError"));
const BizDashboard = lazy(() => import("./routes/bizsol/BizDashboard"));
const BizPatientsList = lazy(() => import("./routes/bizsol/BizPatientsList"));
const BizClinicList = lazy(() => import("./routes/bizsol/BizClinicList"));
const BizCompanyList = lazy(() => import("./routes/bizsol/BizCompanyList"));
const BizClinicDetail = lazy(() => import("./routes/bizsol/BizClinicDetail"));
const BizDeviceManagement = lazy(
  () => import("./routes/bizsol/BizDeviceManagement"),
);
const BizDeviceManagementDetail = lazy(
  () => import("./routes/bizsol/BizDeviceManagementDetail"),
);
const BizInvoiceList = lazy(() => import("./routes/bizsol/BizInvoiceList"));
const BizBasicInfo = lazy(() => import("./routes/bizsol/BizBasicInfo"));
const BizCompanyDetail = lazy(() => import("./routes/bizsol/BizCompanyDetail"));
const BizPasswordChange = lazy(
  () => import("./routes/bizsol/BizPasswordChange"),
);
// 患者詳細（IDあり/なし）を個別 Lazy で読み込み
const BizPatientDetail = lazy(() => import("./routes/bizsol/BizPatientDetail")); // default: BizPatientDetailById
const BizReservationList = lazy(
  () => import("./routes/bizsol/BizReservationsList"),
);
// Patient routes (lazy)
const PatientTaskList = lazy(() => import("./routes/patient/PatientTaskList"));
const PatientHome = lazy(() => import("./routes/patient/PatientHome"));
const PatientReservationCompleted = lazy(
  () => import("./routes/patient/PatientReservationCompleted"),
);
const PatientQuestionnaire = lazy(
  () => import("./routes/patient/PatientQuestionnaire"),
);
const PatientTermsOfUse = lazy(
  () => import("./routes/patient/PatientTermsOfUse"),
);
const PatientProfileSettings = lazy(
  () => import("./routes/patient/PatientProfileSettings"),
);
const PatientPaymentMethod = lazy(
  () => import("./routes/patient/PatientPaymentMethod"),
);
const PatientConveniencePayment = lazy(
  () => import("./routes/patient/PatientConveniencePayment"),
);
const PatientCardChange = lazy(
  () => import("./routes/patient/PatientCardChange"),
);
const PatientChannelChange = lazy(
  () => import("./routes/patient/PatientChannelChange"),
);
const PatientKonbiniChange = lazy(
  () => import("./routes/patient/PatientKonbiniChange"),
);
const PatientPaymentResult = lazy(
  () => import("./routes/patient/PatientPaymentResult"),
);
const PatientCallControls = lazy(
  () => import("./routes/patient/PatientCallControls"),
);
const PatientNotificationList = lazy(
  () => import("./routes/patient/PatientNotificationList"),
);
const PatientReservation = lazy(
  () => import("./routes/patient/PatientReservation"),
);
const PatientClinicSelection = lazy(
  () => import("./routes/patient/PatientClinicSelection"),
);
const PatientClinicSelectionCompleted = lazy(
  () => import("./routes/patient/PatientClinicSelectionCompleted"),
);
const PatientClinicDetail = lazy(
  () => import("./routes/patient/PatientClinicDetail"),
);
const PatientClinicNotFound = lazy(
  () => import("./routes/patient/PatientClinicNotFound"),
);
const PatientClinicSuggestion = lazy(
  () => import("./routes/patient/PatientClinicSuggestion"),
);
const PatientInsuranceUpload = lazy(
  () => import("./routes/patient/PatientInsuranceUpload"),
);
const PatientIdentityUpload = lazy(
  () => import("./routes/patient/PatientIdentityUpload"),
);
const PatientInvoiceList = lazy(
  () => import("./routes/patient/PatientInvoiceList"),
);
const PatientDiagnosticReportList = lazy(
  () => import("./routes/patient/PatientDiagnosticReportList"),
);
const PatientAddressEdit = lazy(
  () => import("./routes/patient/PatientAddressEdit"),
);
const PatientNameEdit = lazy(() => import("./routes/patient/PatientNameEdit"));
const PatientPasswordChange = lazy(
  () => import("./routes/patient/PatientPasswordChange"),
);
const PatientReservationCancelConfirm = lazy(
  () => import("./routes/patient/PatientReservationCancelConfirm"),
);
const PatientPrescriptionConsent = lazy(
  () => import("./routes/patient/PatientPrescriptionConsent"),
);
// Clinic routes
const ClinicDashboard = lazy(() => import("./routes/clinic/ClinicDashboard"));
const ClinicPatientsList = lazy(
  () => import("./routes/clinic/ClinicPatientsList"),
);
const ClinicReservationsList = lazy(
  () => import("./routes/clinic/ClinicReservationsList"),
);
const ClinicReservationSlots = lazy(
  () => import("./routes/clinic/ClinicReservationSlots"),
);
const ClinicOnlineConsultation = lazy(
  () => import("./routes/clinic/ClinicOnlineConsultation"),
);
const ClinicBasicInfo = lazy(() => import("./routes/clinic/ClinicBasicInfo"));
const ClinicInvoiceList = lazy(
  () => import("./routes/clinic/ClinicInvoiceList"),
);
const ClinicPatientDetail = lazy(
  () => import("./routes/clinic/ClinicPatientDetail"),
);
const ClinicPasswordChange = lazy(
  () => import("./routes/clinic/ClinicPasswordChange"),
);
const ClinicTermsOfUse = lazy(() => import("./routes/clinic/ClinicTermsOfUse"));

// Company Admin routes (lazy)
const CompanyAdminDashboard = lazy(
  () => import("./routes/company-admin/CompanyAdminDashboard"),
);
const CompanyAdminEmployeeList = lazy(
  () => import("./routes/company-admin/CompanyAdminEmployeeList"),
);
const CompanyAdminOfficeList = lazy(
  () => import("./routes/company-admin/CompanyAdminOfficeList"),
);
const CompanyAdminEmployeeDetail = lazy(
  () => import("./routes/company-admin/CompanyAdminEmployeeDetail"),
);
const CompanyAdminAdminSettings = lazy(
  () => import("./routes/company-admin/CompanyAdminAdminSettings"),
);
const CompanyAdminNotifications = lazy(
  () => import("./routes/company-admin/CompanyAdminNotifications"),
);
const CompanyAdminBasicSettings = lazy(
  () => import("./routes/company-admin/CompanyAdminBasicSettings"),
);
const CompanyAdminPasswordChange = lazy(
  () => import("./routes/company-admin/CompanyAdminPasswordChange"),
);
const CompanyAdminLinks = lazy(
  () => import("./routes/company-admin/CompanyAdminLinks"),
);
const CompanyAdminTermsOfUse = lazy(
  () => import("./routes/company-admin/CompanyAdminTermsOfUse"),
);

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: () => (
    <>
      <ResponsiveHeader />
      <RoleRedirect>
        <Home />
      </RoleRedirect>
    </>
  ),
});

// Error route helpers
const withErrorShell = (node: React.ReactNode) => (
  <>
    <ResponsiveHeader />
    {node}
  </>
);

const errorRoutes = [
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/forbidden",
    component: () => withErrorShell(<ForbiddenPage />),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/not-found",
    component: () => withErrorShell(<NotFound />),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/server-error",
    component: () => withErrorShell(<ServerError />),
  }),
];

// BizSol route helpers
export function BizsolShell({ children }: { children: React.ReactNode }) {
  const currentPath = useRouterState({ select: (s) => s.location.pathname });
  const [resetKey, setResetKey] = useState(0);
  return (
    <RoleGuard allowedGroups={["ops-admin", "ops-operator"]}>
      <PcHeader />
      <div className="bg-white w-full flex flex-col">
        <div className="w-full flex gap-[32px] items-start p-[30px]">
          <Menu
            title="コンシェルジュメニュー"
            items={bizsolMenuItems}
            currentPath={currentPath}
            onActiveItemClick={() => setResetKey((k) => k + 1)}
          />
          <Fragment key={resetKey}>{children}</Fragment>
        </div>
      </div>
    </RoleGuard>
  );
}

const bizsolRoutes = [
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/bizsol/patients-list",
    component: () => (
      <BizsolShell>
        <BizPatientsList />
      </BizsolShell>
    ),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/bizsol/dashboard",
    component: () => (
      <BizsolShell>
        <BizDashboard />
      </BizsolShell>
    ),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/bizsol/clinic-list",
    component: () => (
      <BizsolShell>
        <BizClinicList />
      </BizsolShell>
    ),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/bizsol/clinic-detail",
    component: () => (
      <BizsolShell>
        <BizClinicDetail />
      </BizsolShell>
    ),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/bizsol/company-list",
    component: () => (
      <BizsolShell>
        <BizCompanyList />
      </BizsolShell>
    ),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/bizsol/device-management",
    component: () => (
      <BizsolShell>
        <BizDeviceManagement />
      </BizsolShell>
    ),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/bizsol/device-detail",
    component: () => (
      <BizsolShell>
        <BizDeviceManagementDetail />
      </BizsolShell>
    ),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/bizsol/invoice-list",
    component: () => (
      <BizsolShell>
        <BizInvoiceList />
      </BizsolShell>
    ),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/bizsol/basic-info",
    component: () => (
      <BizsolShell>
        <BizBasicInfo />
      </BizsolShell>
    ),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/bizsol/company-detail",
    component: () => (
      <BizsolShell>
        <BizCompanyDetail />
      </BizsolShell>
    ),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/bizsol/patient-detail",
    component: () => (
      <BizsolShell>
        <BizPatientDetail />
      </BizsolShell>
    ),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/bizsol/password-change",
    component: () => (
      <BizsolShell>
        <BizPasswordChange />
      </BizsolShell>
    ),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/bizsol/reservation-list",
    component: () => (
      <BizsolShell>
        <BizReservationList />
      </BizsolShell>
    ),
  }),
];

// Patient route helpers
// 患者のフラグが ON なら SAS のセッションで守り（無ければ /patient/login へ）、OFF なら今のまま Cognito の RoleGuard（Issue #473）
const PatientAccessGuard = ({ children }: { children: React.ReactNode }) =>
  isPatientSelfAuthEnabled() ? (
    <PatientSessionGuard>{children}</PatientSessionGuard>
  ) : (
    <RoleGuard allowedGroups={["patient-user"]}>{children}</RoleGuard>
  );

// 患者のログインの画面。フラグが OFF なら /patient/home へ（今の RoleGuard が Cognito へ送る）。
// フラグが ON でも、SAS のセッションがあれば /patient/home へ（ブックマークから開くたびにログインし直させず、
// 別の患者のログインで前の患者のセッションを失効させないまま上書きしないため。古いセッションなら 401 から notice=expired で戻る）。
// ヘッダーは画面の幅によらず SimpleHeader で、戻る矢印を出さない
const PatientLoginRoute = () =>
  isPatientSelfAuthEnabled() && currentPatientSession() === null ? (
    <>
      <SpSimpleHeader showBack={false} />
      <PatientAuthentication />
    </>
  ) : (
    <Navigate to={PATIENT_HOME_PATH} replace />
  );

const withPatientShell = (node: React.ReactNode) => (
  <PatientAccessGuard>
    <OwnPatientDetailProvider>
      <SpHeader />
      <TermsOfUseGuard>{node}</TermsOfUseGuard>
    </OwnPatientDetailProvider>
  </PatientAccessGuard>
);

// 利用規約ページ用: ガードを適用しないシェル
const withPatientShellNoGuard = (node: React.ReactNode) => (
  <PatientAccessGuard>
    <OwnPatientDetailProvider>
      <SpHeader />
      {node}
    </OwnPatientDetailProvider>
  </PatientAccessGuard>
);

// 患者ページ（タイトルあり）用: ガード適用＋カスタムタイトル
const withPatientShellTitle = (title: string, node: React.ReactNode) => (
  <PatientAccessGuard>
    <OwnPatientDetailProvider>
      <SpHeader title={title} />
      <TermsOfUseGuard>{node}</TermsOfUseGuard>
    </OwnPatientDetailProvider>
  </PatientAccessGuard>
);

const withPatientPaymentRoute = (node: React.ReactNode) => (
  <PaymentRouteGuard>{node}</PaymentRouteGuard>
);

// 患者ページ（シンプルヘッダー）用: ガード適用＋メニュー非表示
const withPatientSimpleShell = (node: React.ReactNode) => (
  <PatientAccessGuard>
    <OwnPatientDetailProvider>
      <SpSimpleHeader />
      <TermsOfUseGuard>{node}</TermsOfUseGuard>
    </OwnPatientDetailProvider>
  </PatientAccessGuard>
);

const patientRoutes = [
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/patient/login",
    component: PatientLoginRoute,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/patient/task-list",
    component: () => withPatientShell(<PatientTaskList />),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/patient/home",
    component: () => withPatientShell(<PatientHome />),
  }),

  createRoute({
    getParentRoute: () => rootRoute,
    path: "/patient/reservation/completed",
    component: () => withPatientShell(<PatientReservationCompleted />),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/patient/questionnaire",
    component: () => withPatientShell(<PatientQuestionnaire />),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/patient/terms-of-use",
    component: () => withPatientShellNoGuard(<PatientTermsOfUse />),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/patient/profile",
    component: () => withPatientShell(<PatientProfileSettings />),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/patient/payment/method",
    component: () =>
      withPatientPaymentRoute(
        withPatientShellTitle("支払い方法選択", <PatientPaymentMethod />),
      ),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/patient/payment/convenience",
    component: () =>
      withPatientPaymentRoute(
        withPatientSimpleShell(<PatientConveniencePayment />),
      ),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/patient/payment/card/change",
    component: () =>
      withPatientPaymentRoute(
        withPatientShellTitle("カード変更", <PatientCardChange />),
      ),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/patient/payment/channel/change",
    component: () =>
      withPatientPaymentRoute(
        withPatientShellTitle("支払い方法変更", <PatientChannelChange />),
      ),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/patient/payment/konbini/change",
    component: () =>
      withPatientPaymentRoute(
        withPatientShellTitle("コンビニ変更", <PatientKonbiniChange />),
      ),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/patient/payment/result",
    component: () =>
      withPatientPaymentRoute(withPatientSimpleShell(<PatientPaymentResult />)),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/patient/payment/result/pending",
    component: () =>
      withPatientPaymentRoute(<PaymentResultStatusRedirect status="pending" />),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/patient/payment/result/error",
    component: () =>
      withPatientPaymentRoute(<PaymentResultStatusRedirect status="FAILED" />),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/patient/insurance/upload",
    component: () => withPatientShell(<PatientInsuranceUpload />),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/patient/identity/upload",
    component: () => withPatientShell(<PatientIdentityUpload />),
  }),
  // 患者の請求履歴（Figma 忠実再現用）
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/patient/invoice-list",
    component: () => withPatientShellTitle("請求履歴", <PatientInvoiceList />),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/patient/diagnostic-report/list",
    component: () =>
      withPatientShellTitle(
        "診断レポート一覧",
        <PatientDiagnosticReportList />,
      ),
  }),
  // Address edit uses a custom header title to match Figma
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/patient/address/edit",
    component: () =>
      withPatientShellTitle("住所を編集", <PatientAddressEdit />),
  }),
  // Name edit uses a custom header title to match Figma
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/patient/name/edit",
    component: () => withPatientShellTitle("氏名を編集", <PatientNameEdit />),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/patient/call/controls/$reservationId",
    component: () => withPatientShell(<PatientCallControls />),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/patient/notification-list",
    component: () => withPatientShell(<PatientNotificationList />),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/patient/reservation",
    component: () =>
      withPatientShell(
        <ReservationEligibilityGuard>
          <PatientReservation />
        </ReservationEligibilityGuard>,
      ),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/patient/clinic-selection",
    component: () => withPatientShell(<PatientClinicSelection />),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/patient/clinic-selection/completed",
    component: () => withPatientShell(<PatientClinicSelectionCompleted />),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/patient/clinic-detail",
    component: () => withPatientShell(<PatientClinicDetail />),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/patient/clinic-not-found",
    component: () => withPatientShell(<PatientClinicNotFound />),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/patient/clinic-suggestion",
    component: () => withPatientShell(<PatientClinicSuggestion />),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/patient/reservation-cancel-confirm",
    component: () => withPatientShell(<PatientReservationCancelConfirm />),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/patient/password-change",
    component: () => withPatientShell(<PatientPasswordChange />),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/patient/prescription-consent",
    component: () => withPatientShell(<PatientPrescriptionConsent />),
  }),
];

export function ClinicShell({ children }: { children: React.ReactNode }) {
  const currentPath = useRouterState({ select: (s) => s.location.pathname });
  const [resetKey, setResetKey] = useState(0);
  return (
    <RoleGuard allowedGroups={["clinic-doctor", "clinic-operator"]}>
      <PcHeader />
      <div className="bg-white w-full flex flex-col">
        <div className="w-full flex gap-[30px] items-start p-[30px]">
          <Menu
            title="医療事務メニュー"
            items={clinicMenuItems}
            splitIndex={3}
            currentPath={currentPath}
            onActiveItemClick={() => setResetKey((k) => k + 1)}
          />
          <ClinicTermsOfUseGuard>
            <Fragment key={resetKey}>{children}</Fragment>
          </ClinicTermsOfUseGuard>
        </div>
      </div>
    </RoleGuard>
  );
}

// クリニック利用規約ページ用: ガードを適用しないシェル
export function ClinicShellNoGuard({
  children,
}: {
  children: React.ReactNode;
}) {
  const currentPath = useRouterState({ select: (s) => s.location.pathname });
  const [resetKey, setResetKey] = useState(0);
  return (
    <RoleGuard allowedGroups={["clinic-doctor", "clinic-operator"]}>
      <PcHeader />
      <div className="bg-white w-full flex flex-col">
        <div className="w-full flex gap-[30px] items-start p-[30px]">
          <Menu
            title="医療事務メニュー"
            items={clinicMenuItems}
            splitIndex={3}
            currentPath={currentPath}
            onActiveItemClick={() => setResetKey((k) => k + 1)}
          />
          <Fragment key={resetKey}>{children}</Fragment>
        </div>
      </div>
    </RoleGuard>
  );
}

const clinicRoutes = [
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/clinic",
    component: () => (
      <ClinicShell>
        <ClinicDashboard />
      </ClinicShell>
    ),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/clinic/dashboard",
    component: () => (
      <ClinicShell>
        <ClinicDashboard />
      </ClinicShell>
    ),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/clinic/patient-list",
    component: () => (
      <ClinicShell>
        <ClinicPatientsList />
      </ClinicShell>
    ),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/clinic/reservation-list",
    component: () => (
      <ClinicShell>
        <ClinicReservationsList />
      </ClinicShell>
    ),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/clinic/reservation-slots",
    component: () => (
      <ClinicShell>
        <ClinicReservationSlots />
      </ClinicShell>
    ),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/clinic/online-consultation",
    component: () => (
      <ClinicShell>
        <ClinicOnlineConsultation />
      </ClinicShell>
    ),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/clinic/basic-info",
    component: () => (
      <ClinicShell>
        <ClinicBasicInfo />
      </ClinicShell>
    ),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/clinic/invoice-list",
    component: () => (
      <ClinicShell>
        <ClinicInvoiceList />
      </ClinicShell>
    ),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/clinic/patient-detail",
    component: () => (
      <ClinicShell>
        <ClinicPatientDetail />
      </ClinicShell>
    ),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/clinic/password-change",
    component: () => (
      <ClinicShell>
        <ClinicPasswordChange />
      </ClinicShell>
    ),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/clinic/terms-of-use",
    component: () => (
      <ClinicShellNoGuard>
        <ClinicTermsOfUse />
      </ClinicShellNoGuard>
    ),
  }),
];

// Company Admin route helpers
export function CompanyAdminShell({
  activeItem,
  children,
}: {
  activeItem: string;
  children: React.ReactNode;
}) {
  const [resetKey, setResetKey] = useState(0);
  return (
    <RoleGuard allowedGroups={["patient-admin", "health-nurse"]}>
      <div className="bg-[var(--background\/lightgray,#f1f1f1)] content-stretch flex flex-col items-start relative h-screen overflow-auto">
        <HeaderPc className="bg-[var(--button\/primary,#001973)] content-stretch flex items-center justify-between lg:left-0 lg:sticky lg:z-10 max-lg:w-max min-w-[1024px] px-[var(--sds-size-space-600,24px)] py-[var(--sds-size-space-300,12px)] relative shrink-0 w-full" />
        <div
          className="bg-[var(--background\/lightgray,#f1f1f1)] content-stretch flex flex-[1_0_0] gap-[var(--数値,24px)] items-start min-h-px min-w-[1024px] p-[24px] relative w-full"
          data-name="Body"
        >
          <SidebarMenu
            title="企業管理者メニュー"
            sections={companyAdminMenuSections}
            activeItem={activeItem}
            onActiveItemClick={() => setResetKey((k) => k + 1)}
          />
          <CompanyAdminTermsOfUseGuard>
            <Fragment key={resetKey}>{children}</Fragment>
          </CompanyAdminTermsOfUseGuard>
        </div>
      </div>
    </RoleGuard>
  );
}

// 企業管理者向け利用規約ページ用: ガードを適用しないシェル
export function CompanyAdminShellNoGuard({
  activeItem,
  children,
}: {
  activeItem: string;
  children: React.ReactNode;
}) {
  const [resetKey, setResetKey] = useState(0);
  return (
    <RoleGuard allowedGroups={["patient-admin", "health-nurse"]}>
      <div className="bg-[var(--background\/lightgray,#f1f1f1)] content-stretch flex flex-col items-start relative h-screen overflow-auto">
        <HeaderPc className="bg-[var(--button\/primary,#001973)] content-stretch flex items-center justify-between lg:left-0 lg:sticky lg:z-10 max-lg:w-max min-w-[1024px] px-[var(--sds-size-space-600,24px)] py-[var(--sds-size-space-300,12px)] relative shrink-0 w-full" />
        <div
          className="bg-[var(--background\/lightgray,#f1f1f1)] content-stretch flex flex-[1_0_0] gap-[var(--数値,24px)] items-start min-h-px min-w-[1024px] p-[24px] relative w-full"
          data-name="Body"
        >
          <SidebarMenu
            title="企業管理者メニュー"
            sections={companyAdminMenuSections}
            activeItem={activeItem}
            onActiveItemClick={() => setResetKey((k) => k + 1)}
          />
          <Fragment key={resetKey}>{children}</Fragment>
        </div>
      </div>
    </RoleGuard>
  );
}

const companyAdminRoutes = [
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/company-admin/dashboard",
    component: () => (
      <CompanyAdminShell activeItem="ダッシュボード">
        <CompanyAdminDashboard />
      </CompanyAdminShell>
    ),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/company-admin/employees",
    component: () => (
      <CompanyAdminShell activeItem="従業員一覧">
        <CompanyAdminEmployeeList />
      </CompanyAdminShell>
    ),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/company-admin/offices",
    component: () => (
      <CompanyAdminShell activeItem="事業所一覧">
        <CompanyAdminOfficeList />
      </CompanyAdminShell>
    ),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/company-admin/employee-detail",
    component: () => (
      <CompanyAdminShell activeItem="">
        <CompanyAdminEmployeeDetail />
      </CompanyAdminShell>
    ),
  }),
  /* 管理者自身でユーザーを追加できる機能は9/16リリース対象外のため、該当ルートをコメントアウト
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/company-admin/admin-settings",
    component: () => (
      <CompanyAdminShell activeItem="管理者設定">
        <CompanyAdminAdminSettings />
      </CompanyAdminShell>
    ),
  }),
  */
  /* お知らせを確認できる機能は9/16リリース対象外のため、該当ルートをコメントアウト
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/company-admin/notifications",
    component: () => (
      <CompanyAdminShell activeItem="お知らせ">
        <CompanyAdminNotifications />
      </CompanyAdminShell>
    ),
  }),
  */
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/company-admin/basic-settings",
    component: () => (
      <CompanyAdminShell activeItem="基本設定">
        <CompanyAdminBasicSettings />
      </CompanyAdminShell>
    ),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/company-admin/password-change",
    component: () => (
      <CompanyAdminShell activeItem="パスワード変更">
        <CompanyAdminPasswordChange />
      </CompanyAdminShell>
    ),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/company-admin/links",
    component: () => (
      <CompanyAdminShell activeItem="リンク集">
        <CompanyAdminLinks />
      </CompanyAdminShell>
    ),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/company-admin/terms-of-use",
    component: () => (
      <CompanyAdminShellNoGuard activeItem="">
        <CompanyAdminTermsOfUse />
      </CompanyAdminShellNoGuard>
    ),
  }),
];

const routeTree = rootRoute.addChildren([
  indexRoute,
  ...errorRoutes,
  ...bizsolRoutes,
  ...patientRoutes,
  ...clinicRoutes,
  ...companyAdminRoutes,
]);

export function createAppRouter() {
  return createRouter({ routeTree });
}

export const router = createAppRouter();

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

export function AppRouter({
  appRouter = router,
}: {
  appRouter?: typeof router;
}) {
  return <RouterProvider router={appRouter} />;
}
